import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getRadioRanking,
  getProgramRanking,
  getHostRanking,
  getFanRanking,
  trendConfig,
  type DisplayRankingEntry,
} from '@/mocks/rfwScores';

type TopTab = 'radios' | 'programas' | 'locutores' | 'fans';

const tabConfig: Record<TopTab, { label: string; icon: string; color: string }> = {
  radios: { label: 'Top Radios', icon: 'ri-radio-line', color: 'text-primary-600' },
  programas: { label: 'Top Programas', icon: 'ri-calendar-check-line', color: 'text-accent-600' },
  locutores: { label: 'Top Locutores', icon: 'ri-mic-line', color: 'text-secondary-600' },
  fans: { label: 'Top Fans', icon: 'ri-heart-pulse-line', color: 'text-rose-500' },
};

function formatScore(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function SmallTrendBadge({ trend, scoreChange }: { trend: string; scoreChange: number }) {
  const cfg = trendConfig[trend as keyof typeof trendConfig];
  if (!cfg) return null;
  const isUp = scoreChange > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${isUp ? 'text-emerald-600' : scoreChange < 0 ? 'text-red-500' : 'text-foreground-400'}`}>
      <i className={`${isUp ? 'ri-arrow-up-fill' : scoreChange < 0 ? 'ri-arrow-down-fill' : 'ri-subtract-line'} text-[10px]`}></i>
      {scoreChange > 0 ? '+' : ''}{scoreChange}
    </span>
  );
}

function TrophyIcon({ pos }: { pos: number }) {
  if (pos === 1) return <span className="text-base">🥇</span>;
  if (pos === 2) return <span className="text-base">🥈</span>;
  if (pos === 3) return <span className="text-base">🥉</span>;
  return (
    <span className="w-6 h-6 rounded-full bg-background-100 flex items-center justify-center text-[11px] font-extrabold text-foreground-400">
      {pos}
    </span>
  );
}

function TopRankingCard({ entry, pos }: { entry: DisplayRankingEntry; pos: number }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 hover:bg-background-100/70 transition-colors group cursor-pointer">
      {/* Position */}
      <div className="shrink-0">
        <TrophyIcon pos={pos} />
      </div>

      {/* Avatar */}
      <div className="w-10 h-10 rounded-xl overflow-hidden bg-background-100 shrink-0 border border-background-200/70">
        {entry.logoUrl ? (
          <img src={entry.logoUrl} alt={entry.entityName} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="ri-user-line text-sm text-foreground-300"></i>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-foreground-950 truncate group-hover:text-primary-600 transition-colors">
          {entry.entityName}
        </p>
        <p className="text-[11px] text-foreground-400 truncate">{entry.entitySubtitle}</p>
      </div>

      {/* Score */}
      <div className="shrink-0 flex flex-col items-end gap-0.5">
        <span className="text-sm font-extrabold text-foreground-950">{formatScore(entry.score)}</span>
        <SmallTrendBadge trend={entry.trend} scoreChange={entry.scoreChange} />
      </div>
    </div>
  );
}

export default function TopRankingsSection() {
  const [activeTab, setActiveTab] = useState<TopTab>('radios');
  const navigate = useNavigate();

  const entries = useMemo((): DisplayRankingEntry[] => {
    switch (activeTab) {
      case 'radios': return getRadioRanking('week').slice(0, 10);
      case 'programas': return getProgramRanking('week').slice(0, 10);
      case 'locutores': return getHostRanking('week').slice(0, 10);
      case 'fans': return getFanRanking('week').slice(0, 10);
      default: return [];
    }
  }, [activeTab]);

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
              <i className="ri-flashlight-fill text-xs"></i>
              RFW Score Rankings
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
              Los Mejores de<br />
              <span className="text-accent-600">RadiofrecuenciaWEB</span>
            </h2>
            <p className="mt-3 text-sm text-foreground-500 max-w-lg leading-relaxed">
              Rankings basados en comunidad e interacción real. No solo audiencia: medimos participación, engagement y crecimiento.
            </p>
          </div>
          <button
            onClick={() => navigate('/rankings')}
            className="text-primary-600 hover:text-primary-700 font-semibold text-sm flex items-center gap-1 whitespace-nowrap cursor-pointer"
          >
            Ver todos los rankings
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {(Object.keys(tabConfig) as TopTab[]).map((tab) => {
            const cfg = tabConfig[tab];
            const isActive = tab === activeTab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'bg-primary-500 text-background-50 shadow-lg'
                    : 'bg-background-50 text-foreground-500 hover:text-foreground-700 border border-background-200'
                }`}
              >
                <i className={`${cfg.icon} text-sm`}></i>
                {cfg.label}
              </button>
            );
          })}
        </div>

        {/* Ranking List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {entries.map((entry, idx) => (
            <TopRankingCard key={`${entry.entityId}-${idx}`} entry={entry} pos={idx + 1} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/rankings')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 border border-background-200 hover:border-primary-200 rounded-xl text-sm font-bold text-foreground-700 hover:text-primary-600 transition-all cursor-pointer whitespace-nowrap"
          >
            Explorar Rankings Completos
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}